import { cn } from "@/lib/utils";
import { CheckCircle, Circle, AlertCircle, XCircle } from "lucide-react";

export interface ProgressStep {
  id: string;
  title: string;
  description?: string;
  status: "pending" | "current" | "completed" | "error" | "skipped";
  optional?: boolean;
}

interface ProgressTrackerProps {
  steps: ProgressStep[];
  className?: string;
  orientation?: "horizontal" | "vertical";
  showDescriptions?: boolean;
}

export default function ProgressTracker({
  steps,
  className,
  orientation = "horizontal",
  showDescriptions = false
}: ProgressTrackerProps) {
  const getStepIcon = (step: ProgressStep) => {
    const iconProps = { className: "w-5 h-5" };

    switch (step.status) {
      case "completed":
        return <CheckCircle {...iconProps} className="w-5 h-5 text-green-600" />;
      case "current":
        return (
          <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
          </div>
        );
      case "error":
        return <XCircle {...iconProps} className="w-5 h-5 text-red-600" />;
      case "skipped":
        return <AlertCircle {...iconProps} className="w-5 h-5 text-gray-400" />;
      default:
        return <Circle {...iconProps} className="w-5 h-5 text-gray-300" />;
    }
  };

  const getStepStyles = (step: ProgressStep) => {
    const baseStyles = "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors";

    switch (step.status) {
      case "completed":
        return cn(baseStyles, "border-green-600 bg-green-50");
      case "current":
        return cn(baseStyles, "border-blue-600 bg-blue-50");
      case "error":
        return cn(baseStyles, "border-red-600 bg-red-50");
      case "skipped":
        return cn(baseStyles, "border-gray-300 bg-gray-50");
      default:
        return cn(baseStyles, "border-gray-300 bg-gray-50");
    }
  };

  const getConnectorStyles = (currentIndex: number, nextStep?: ProgressStep) => {
    const baseStyles = "flex-1 h-0.5 transition-colors";

    if (!nextStep) return "";

    const currentStep = steps[currentIndex];
    if (currentStep.status === "completed" || nextStep.status === "completed" || nextStep.status === "current") {
      return cn(baseStyles, "bg-green-600");
    }

    return cn(baseStyles, "bg-gray-300");
  };

  const getTextStyles = (step: ProgressStep) => {
    switch (step.status) {
      case "completed":
        return "text-green-800";
      case "current":
        return "text-blue-800 font-medium";
      case "error":
        return "text-red-800";
      case "skipped":
        return "text-gray-500";
      default:
        return "text-gray-600";
    }
  };

  if (orientation === "vertical") {
    return (
      <div className={cn("space-y-4", className)}>
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start gap-4">
            <div className="flex flex-col items-center">
              <div className={getStepStyles(step)}>
                {getStepIcon(step)}
              </div>
              {index < steps.length - 1 && (
                <div className="w-0.5 h-8 mt-2 bg-gray-300" />
              )}
            </div>
            <div className="flex-1 pb-4">
              <h3 className={cn("text-sm font-medium", getTextStyles(step))}>
                {step.title}
                {step.optional && (
                  <span className="ml-2 text-xs text-gray-500">(optional)</span>
                )}
              </h3>
              {showDescriptions && step.description && (
                <p className="text-xs text-gray-600 mt-1">{step.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center w-full", className)}>
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center flex-1">
          <div className="flex flex-col items-center">
            <div className={getStepStyles(step)}>
              {getStepIcon(step)}
            </div>
            <div className="mt-2 text-center">
              <p className={cn("text-xs font-medium", getTextStyles(step))}>
                {step.title}
                {step.optional && (
                  <span className="block text-gray-500">(optional)</span>
                )}
              </p>
              {showDescriptions && step.description && (
                <p className="text-xs text-gray-600 mt-1 max-w-20">
                  {step.description}
                </p>
              )}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div className={getConnectorStyles(index, steps[index + 1])} />
          )}
        </div>
      ))}
    </div>
  );
}